import { InfoCircleOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import moment from 'moment'
import { useCallback } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { handleException } from 'clients/exception-handling'
import { SourceProps } from 'features/project-form/features/source/components/SourceProviders'

interface FBAuthResponse {
  userID: string
  accessToken: string
}

interface FBLoginResponse {
  authResponse?: FBAuthResponse
}

interface FBPageObject {
  id: string
  access_token: string
}

interface FBApiResponse<T> {
  data?: T
}

const dateFormat = 'MM/DD/YYYY'
type FBType = {
  login: (
    cb: (r: FBLoginResponse) => void,
    opts: Record<string, string>,
  ) => void
  api: (
    path: string,
    cb: (response: FBApiResponse<FBPageObject[]>) => void,
  ) => void
}

export const Instagram = ({ formik }: SourceProps): JSX.Element => {
  const connect = useCallback(async () => {
    const FB = window.FB as FBType | undefined
    new Promise<FBAuthResponse>((resolve, reject) => {
      FB!.login(
        function (r: FBLoginResponse) {
          if (r.authResponse) {
            resolve(r.authResponse)
          } else {
            reject(false)
          }
        },
        { scope: 'instagram_basic,pages_read_engagement,pages_show_list' },
      )
    })
      .then(
        (authResponse: FBAuthResponse) =>
          new Promise<FBPageObject[]>((resolve, reject) => {
            FB!.api(
              `${authResponse.userID}/accounts`,
              function (response: FBApiResponse<FBPageObject[]>) {
                if (response.data) {
                  resolve(response.data)
                } else {
                  reject(false)
                }
              },
            )
          }),
      )
      .then((pageObject: FBPageObject[]) => {
        const firstPage = pageObject[0]
        if (!firstPage) return
        FB!.api(
          `${firstPage.id}?fields=instagram_business_account`,
          function (_response: FBApiResponse<unknown>) {
            formik.setFieldValue('access_token', firstPage.access_token)
            formik.setFieldValue('page_id', firstPage.id)
          },
        )
      })
      .catch((_e: unknown) => {
        handleException(_e, {
          operation: 'project.source.instagram.authorize',
          disposition: 'expected',
          isExpectedError: (cause) => cause === false,
          reason: 'User declined Instagram authorization.',
        })

        alert(
          'Something went wrong. Please try again from a new tab, or try again with a hard refresh (Shift-Ctrl-R)',
        )
      })
  }, [formik])

  return (
    <>
      <FormItem
        label={
          <Tooltip content="Give your source a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceTitle ? 'error' : ''}
        help={formik.errors.sourceTitle}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(formik.errors.sourceTitle)}
          placeholder="Insert title here"
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Select the earliest date from which data should be pulled.">
            <span>
              Date <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
        validateStatus={formik.errors.sourceDate ? 'error' : ''}
        help={formik.errors.sourceDate}
      >
        <DatePicker
          defaultValue={
            formik.values.sourceDate && moment(formik.values.sourceDate)
          }
          onChange={(value) => {
            if (value) {
              formik.setFieldValue('sourceDate', value.toDate())
            }
          }}
          format={dateFormat}
        />
      </FormItem>
      <ButtonWrapper>
        <Error>{formik.submitCount > 0 && formik.errors.access_token}</Error>
        <Button variant="primary" size="md" onClick={connect}>
          Connect with Facebook
        </Button>
      </ButtonWrapper>
    </>
  )
}

const ButtonWrapper = styled.div`
  max-width: 200px;
  margin: 0 auto;
`
const Error = styled.div`
  text-align: center;
  color: #ff4d4f;
`
